import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CommonDrawerLayout from '../../../common_components/CommonDrawerLayout';
import AppButton from '../../../common_components/AppButton';
import { ExportButton } from '../../../common_components/FormInputs';
import FormattedMeetingText from './FormattedMeetingText';
import AgendaDocument from './AgendaDocument';
import { fetchAgendaById, clearCurrentAgenda, updateAgendaDocumentThunk, apiErrorMessage, selectCurrentAgenda, selectCurrentAgendaLoading, } from '../../../redux/meetingAgendaSlice';
import { selectToken } from '../../../redux/authSlice';
import meetingAgendaService from '../../../services/meetingAgenda.service';
import { toast } from '../../../common_components/Toast/ToastUtils';
// US Eastern, matching the meetings picker and the rest of the portal.
const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});
const fmtDateTime = (iso) => {
    if (!iso)
        return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime()))
        return '—';
    return `${DATE_FORMATTER.format(d)} EST`;
};
const Field = ({ label, value }) => (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.25 }, children: [_jsx(Typography, { sx: { fontSize: 12, color: '#667085', fontWeight: 500 }, children: label }), _jsx(Typography, { sx: { fontSize: 14, color: '#101828' }, children: value })] }));
const MeetingRow = ({ meeting }) => (_jsxs(Box, { sx: {
        border: '1px solid #EAECF0',
        borderRadius: '8px',
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
    }, children: [_jsx(Typography, { sx: { fontSize: 14, fontWeight: 600, color: '#101828' }, children: meeting.title || '(untitled meeting)' }), _jsx(Typography, { sx: { fontSize: 13, color: '#667085' }, children: fmtDateTime(meeting.date) }), meeting.bullet_gist && (_jsxs(Box, { sx: { mt: 0.5 }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 600, color: '#344054' }, children: "Key Points" }), _jsx(FormattedMeetingText, { text: meeting.bullet_gist, fontSize: "13px" })] })), meeting.action_items && (_jsxs(Box, { sx: { mt: 0.5 }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 600, color: '#344054' }, children: "Action Items" }), _jsx(FormattedMeetingText, { text: meeting.action_items, fontSize: "13px" })] }))] }));
const ViewAgendaDrawer = ({ open, agendaId, onClose }) => {
    const dispatch = useDispatch();
    const agenda = useSelector(selectCurrentAgenda);
    const loading = useSelector(selectCurrentAgendaLoading);
    const token = useSelector(selectToken);
    const [downloading, setDownloading] = useState(false);
    // Editing state. `draft` is null until something is changed, so the
    // rendered document falls back to what is stored and "Save" knows whether
    // there is anything to send.
    const [editing, setEditing] = useState(false);
    const [drafts, setDrafts] = useState({
        worksheet: null,
        agenda: null,
    });
    const setDraftFor = (key) => (markdown) => setDrafts((prev) => ({ ...prev, [key]: markdown }));
    const clearDrafts = () => setDrafts({ worksheet: null, agenda: null });
    /** Which documents have unsaved changes right now. */
    const dirtyDocs = Object.keys(drafts).filter((k) => drafts[k] !== null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (open && agendaId) {
            dispatch(fetchAgendaById(agendaId));
        }
        if (!open) {
            dispatch(clearCurrentAgenda());
        }
    }, [open, agendaId, dispatch]);
    const meetings = agenda?.meetings || [];
    const report = agenda?.report || null;
    // Internal Worksheet first, Client Agenda second — the order the Build
    // Prompt delivers them in.
    const [tab, setTab] = useState('worksheet');
    // Depends on WHETHER a report exists, not on the report object. Keying
    // this on `report` re-fired whenever the agenda was replaced in the store
    // — so saving an edit to the Client Agenda silently threw you back to the
    // Worksheet tab, which read as the text resetting.
    const hasReport = !!report;
    useEffect(() => {
        // Without documents (a generation that failed) there's only the
        // meeting list to show.
        setTab(hasReport ? 'worksheet' : 'meetings');
    }, [hasReport, agendaId]);
    const TABS = [
        { id: 'worksheet', label: 'Internal Worksheet', hidden: !report },
        { id: 'client', label: 'Client Agenda', hidden: !report },
        { id: 'meetings', label: `Meetings (${meetings.length})` },
    ];
    /**
     * Word export of whichever document is on screen. Downloading the active
     * tab means the button never needs to ask which one — what you are reading
     * is what you get.
     */
    const downloadableDoc = tab === 'worksheet' ? 'worksheet' : tab === 'client' ? 'agenda' : null;
    // Only a different agenda abandons drafts. Switching TABS keeps them —
    // edit the worksheet, flip to the agenda, come back, and the first set of
    // changes is still there waiting to be saved.
    useEffect(() => {
        setEditing(false);
        clearDrafts();
    }, [agendaId]);
    /** Saves every edited document in one call, whichever tab is open. */
    const handleSaveEdits = async () => {
        if (!agendaId || !dirtyDocs.length) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            const res = await dispatch(updateAgendaDocumentThunk({
                agendaId,
                documents: dirtyDocs.map((key) => ({
                    doc: key,
                    markdown: drafts[key],
                })),
            })).unwrap();
            toast.success(res?.message || 'Document saved.');
            setEditing(false);
            clearDrafts();
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : err?.message || 'Could not save the document.');
        }
        finally {
            setSaving(false);
        }
    };
    const handleDownload = async () => {
        if (!agendaId || !downloadableDoc)
            return;
        setDownloading(true);
        try {
            const response = await meetingAgendaService.downloadAgendaDocx(agendaId, downloadableDoc, token || '');
            if (!response || response.status !== 200 || !response.data) {
                // responseType is 'blob', so a failure arrives as a Blob of
                // JSON rather than a parsed body — read it back so the user
                // sees the real reason instead of "download failed".
                const payload = response?.response?.data;
                if (payload instanceof Blob) {
                    const text = await payload.text().catch(() => '');
                    try {
                        const parsed = JSON.parse(text);
                        if (parsed?.message)
                            throw new Error(parsed.message);
                    }
                    catch (parseErr) {
                        if (parseErr?.message && parseErr.message !== text)
                            throw parseErr;
                    }
                }
                throw new Error(apiErrorMessage(response, 'Could not build the Word file.'));
            }
            // The server names the file (it knows the reporting window), so
            // read it back rather than rebuilding the name here.
            const disposition = response.headers?.['content-disposition'] ||
                response.headers?.['Content-Disposition'] ||
                '';
            const named = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = named || `${agenda?.client_name || 'agenda'}_${downloadableDoc}.docx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : err?.message || 'Could not build the Word file.');
        }
        finally {
            setDownloading(false);
        }
    };
    return (_jsx(CommonDrawerLayout, { open: open, className: "Meeting_Agenda_drawer", onClose: onClose, title: "Meeting Agenda", subtitle: agenda?.client_name || '', isLoading: loading, width: "xl", headerActions: 
        // Only on a document tab — there is nothing to export from the
        // meeting list.
        downloadableDoc ? (_jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' }, children: [editing ? (_jsxs(_Fragment, { children: [_jsx(AppButton, { variant: "contained", onClick: handleSaveEdits, loading: saving, disabled: saving, sx: {
                                bgcolor: '#FF008E', textTransform: 'none',
                                fontWeight: 600, borderRadius: '8px', height: 36,
                                '&:hover': { bgcolor: '#E6007A' },
                            }, children: "Save" }), _jsx(AppButton, { variant: "outlined", onClick: () => { setEditing(false); clearDrafts(); }, disabled: saving, sx: {
                                textTransform: 'none', borderRadius: '8px',
                                height: 36, color: '#344054', borderColor: '#D0D5DD',
                            }, children: "Cancel" })] })) : (_jsx(AppButton, { variant: "outlined", onClick: () => setEditing(true), sx: {
                        textTransform: 'none', borderRadius: '8px',
                        height: 36, color: '#344054', borderColor: '#D0D5DD',
                    }, children: "Edit" })), _jsx(ExportButton, { onClick: handleDownload, loading: downloading, disabled: editing, label: downloadableDoc === 'worksheet'
                        ? 'Download Worksheet'
                        : 'Download Agenda' })] })) : null, children: _jsx(Box, { sx: { p: 1, bgcolor: '#F9FAFB', minHeight: '100%' }, children: _jsxs(Box, { sx: {
                    bgcolor: '#fff',
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }, children: [_jsxs(Box, { sx: { display: 'flex', gap: 3, flexWrap: 'wrap' }, children: [_jsx(Field, { label: "Client", value: agenda?.client_name || '—' }), _jsx(Field, { label: "Created By", value: agenda?.created_by_name || '—' }), _jsx(Field, { label: "Date", value: fmtDateTime(agenda?.created_at) }), report?.generated_at && (_jsx(Field, { label: "Prepared", value: fmtDateTime(report.generated_at) }))] }), !report && !loading && (_jsx(Typography, { sx: {
                            fontSize: 12.5,
                            color: '#B54708',
                            bgcolor: '#FFFAEB',
                            border: '1px solid #FEDF89',
                            borderRadius: '8px',
                            px: 1.5,
                            py: 1,
                        }, children: "The documents for this agenda haven\u2019t been generated yet \u2014 use \u201CPrepare Agenda\u201D on the list row to build them." })), _jsx(Box, { sx: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 0.5,
                            bgcolor: '#F2F4F7',
                            borderRadius: '8px',
                            p: 0.5,
                        }, children: TABS.filter((t) => !t.hidden).map((t) => (_jsxs(Box, { onClick: () => setTab(t.id), sx: {
                                px: 2,
                                py: 1,
                                borderRadius: '6px',
                                fontSize: 13,
                                fontWeight: 600,
                                textAlign: 'center',
                                cursor: 'pointer',
                                bgcolor: tab === t.id ? '#FF008E' : 'transparent',
                                color: tab === t.id ? '#fff' : '#101828',
                            }, children: [t.label, ((t.id === 'worksheet' && drafts.worksheet !== null) ||
                                    (t.id === 'client' && drafts.agenda !== null)) && (_jsx(Box, { component: "span", title: "Unsaved changes", sx: {
                                        display: 'inline-block',
                                        width: 7,
                                        height: 7,
                                        ml: 0.75,
                                        borderRadius: '50%',
                                        bgcolor: tab === t.id ? '#fff' : '#FF008E',
                                        verticalAlign: 'middle',
                                    } }))] }, t.id))) }), tab === 'meetings' && (meetings.length === 0 ? (_jsx(Typography, { sx: { fontSize: 13, color: '#667085' }, children: "No meetings were attached to this agenda." })) : (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: meetings.map((m) => (_jsx(MeetingRow, { meeting: m }, m.transcript_id))) }))), tab === 'worksheet' && report && (_jsx(AgendaDocument, { markdown: drafts.worksheet ?? report.internal_worksheet, editable: editing, onChange: setDraftFor('worksheet') })), tab === 'client' && report && (_jsx(AgendaDocument, { markdown: drafts.agenda ?? report.client_agenda, editable: editing, onChange: setDraftFor('agenda') })), report?.data_notes?.length && tab !== 'meetings' ? (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 600, color: '#344054', mb: 0.5 }, children: "Data sourcing notes & limitations" }), _jsx(Box, { component: "ul", sx: { m: 0, pl: 2.5 }, children: report.data_notes.map((note, i) => (_jsx(Typography, { component: "li", sx: { fontSize: 13, color: '#475467', lineHeight: 1.5 }, children: note }, i))) })] })] })) : null] }) }) }));
};
export default ViewAgendaDrawer;
